// Tremor Callout [v0.0.1]

import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

import { cn } from "@/lib/utils";

const calloutVariants = cva("flex flex-col rounded-md p-4 text-sm", {
  variants: {
    variant: {
      default:
        "border border-blue-300 dark:border-blue-500/50 text-blue-900 dark:text-blue-400 bg-blue-50 dark:bg-blue-950",
      success:
        "border border-emerald-300 dark:border-emerald-500/50 text-emerald-900 dark:text-emerald-500 bg-emerald-50 dark:bg-emerald-950",
      error:
        "text-destructive bg-destructive/10 dark:bg-destructive/10 dark:border-destructive/30 border border-destructive",
      warning:
        "border border-amber-300 dark:border-amber-500/50 text-yellow-900 dark:text-yellow-400 bg-yellow-50 dark:bg-amber-950",
      neutral:
        "border border-gray-300 dark:border-gray-500/50 text-foreground bg-muted dark:bg-muted"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});

interface CalloutProps
  extends React.ComponentPropsWithoutRef<"div">,
    VariantProps<typeof calloutVariants> {
  title: string;
  icon?: React.ElementType | React.ReactElement;
}

const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(
  ({ title, icon: Icon, className, variant, children, ...props }: CalloutProps, forwardedRef) => {
    return (
      <div
        role="alert"
        data-slot="alert"
        ref={forwardedRef}
        className={cn(calloutVariants({ variant }), className)}
        {...props}
      >
        <div className={cn("flex items-center")}>
          {Icon && React.isValidElement(Icon) ? (
            Icon
          ) : Icon ? (
            <Icon className={cn("mr-1.5 h-4.5 w-4.5 shrink-0")} aria-hidden={true} />
          ) : null}
          <span data-slot="alert-title" className={cn("text-base font-semibold")}>
            {title}
          </span>
        </div>
        <div className={cn("font-semibold", children ? "mt-2" : "")}>{children}</div>
      </div>
    );
  }
);

Callout.displayName = "Callout";

export { Callout, calloutVariants, type CalloutProps };
